import { useCallback, useSyncExternalStore } from 'react';

import type { ToolUseItem } from '../../../../../contracts/harness.types';
import type { EvidenceTrail, EvidenceTrails } from './evidenceTrail';

const unsubscribed = () => undefined;
const noTrail = () => null;

/**
 * An Evidence step's trail, re-read whenever the app says it may have changed.
 * With no step there is no subscription, and a step only watches what can
 * still change for it. The port's functions decide the subscription, so a
 * host may rebuild the port object around the same functions.
 */
export const useEvidenceTrail = (
  trails: EvidenceTrails | undefined,
  step: ToolUseItem | null
): EvidenceTrail | null => {
  // A step re-folds into a new object on every transcript change; what it
  // watches depends only on these, and an unchanged result keeps its object.
  const id = step?.id;
  const status = step?.status;
  const childAgentRunId = step?.childAgentRunId;
  const saved = step?.result_provider_metadata;
  const watch = trails?.subscribe;
  const read = trails?.read;
  const subscribe = useCallback(
    (onChange: () => void) =>
      watch && id !== undefined && status !== undefined
        ? watch(
            { id, status, childAgentRunId, result_provider_metadata: saved },
            onChange
          )
        : unsubscribed,
    [watch, id, status, childAgentRunId, saved]
  );
  return useSyncExternalStore(
    subscribe,
    () => (read && step ? read(step) : null),
    noTrail
  );
};
